import React, { FC, useState } from "react";
import { TaskResponse } from "@9amhealth/openapi";
import { useBlocNext } from "@blac/react";
import styled from "@emotion/styled";
import { KnownProgram } from "src/state/ProgramBloc/ProgramBloc";
import TaskManagementBloc, {
  TaskKey
} from "src/state/TaskManagementBloc/TaskManagementBloc";
import RxDietFirstScreen from "src/ui/components/Meals/RxDietFirstScreen";
import RxDietDescriptionStep from "src/ui/components/Meals/RxDietDescriptionStep";
import RxDietIframe from "src/ui/components/Meals/RxDietIframe";

const PageWrap = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

type MealsStep = "firstScreen" | "description" | "iframe";

export const Meals: FC = () => {
  const [, { getProgramTasks, loadProgramTasks, isProgramLoaded }] =
    useBlocNext(TaskManagementBloc);
  const [currentStep, setCurrentStep] = useState<MealsStep>("firstScreen");
  const [isTaskLoaded, setIsTaskLoaded] = useState(false);

  const [createFirstMealPlanTaskStatus, setCreateFirstMealPlanTaskStatus] =
    React.useState<TaskResponse.status | null>(null);

  const mealPlanTasks = getProgramTasks(KnownProgram.MEAL_PLAN);
  const programAlreadyLoaded = isProgramLoaded(KnownProgram.MEAL_PLAN);

  React.useEffect(() => {
    if (programAlreadyLoaded) {
      setIsTaskLoaded(true);
      return;
    }

    void loadProgramTasks(KnownProgram.MEAL_PLAN).finally(() => {
      setIsTaskLoaded(true);
    });
  }, [programAlreadyLoaded, loadProgramTasks]);

  React.useEffect(() => {
    if (mealPlanTasks.length) {
      const createFirstMealPlanTask = mealPlanTasks.find(
        (task) =>
          `${task.program}.${task.group}.${task.slug}` ===
          TaskKey.RXDIET_MEAL_PLAN_SETUP
      );

      if (createFirstMealPlanTask) {
        setCreateFirstMealPlanTaskStatus(createFirstMealPlanTask.status);
      }
    }
  }, [mealPlanTasks]);

  const handleFirstScreenContinue = () => {
    setCurrentStep("description");
  };

  const handleDescriptionContinue = () => {
    setCurrentStep("iframe");
  };

  const isTaskAvailable =
    createFirstMealPlanTaskStatus === TaskResponse.status.AVAILABLE;

  const shouldShowFirstScreen =
    isTaskAvailable && currentStep === "firstScreen";
  const shouldShowDescription =
    isTaskAvailable && currentStep === "description";
  const shouldShowIframe = currentStep === "iframe" || !isTaskAvailable;

  return (
    <PageWrap>
      {shouldShowFirstScreen && (
        <RxDietFirstScreen onContinue={handleFirstScreenContinue} />
      )}
      {shouldShowDescription && (
        <RxDietDescriptionStep onContinue={handleDescriptionContinue} />
      )}
      {shouldShowIframe && <RxDietIframe />}
    </PageWrap>
  );
};

export default Meals;
